import { baseApi } from '@/api/baseApi';

import type {
  ApproveFfneRequest,
  ChangePdfNameRequest,
  CreateFfneRequest,
  DuplicateFfneRequest,
  FfneDetailsData,
  FfneItem,
  GeneratePdfRequest,
  GetCollectionProductsRequest,
  GetFfneListRequest,
  GetPdfDataRequest,
  GetShopifyFiltersRequest,
  PatchFfneRequest,
  PdfRequest,
  RejectFfneRequest,
  SyncFfneAreasRequest,
  SyncFfneProductsRequest,
} from './types';

const PS_V1 = 'ps/v1';

export const ffneApi = baseApi.injectEndpoints({
  endpoints: build => ({
    // ── Queries ────────────────────────────────────────────────────────────────

    getFfneList: build.query<FfneItem[], GetFfneListRequest>({
      query: ({ projectId, status, search }) => ({
        url: `${PS_V1}/ffne`,
        params: {
          projectId,
          ...(status ? { status } : {}),
          ...(search ? { search } : {}),
        },
      }),
      transformResponse: (res: any) => res?.data ?? res,
      providesTags: ['Ffne'],
    }),

    getFfneById: build.query<FfneDetailsData, string>({
      query: ffneId => ({ url: `${PS_V1}/ffne/project/${ffneId}/areas` }),
      transformResponse: (res: any) => res?.data ?? res,
      providesTags: ['Ffne'],
    }),

    getFfneCategories: build.query<unknown, void>({
      query: () => ({ url: `${PS_V1}/ffne/shopify-collection-menu` }),
      transformResponse: (res: any) => res?.data ?? res,
    }),

    getShopifyCollectionFilters: build.query<unknown, GetShopifyFiltersRequest | undefined>({
      query: arg => ({
        url: `${PS_V1}/ffne/shopify-collection-filters`,
        params: arg?.collectionHandle ? { collectionHandle: arg.collectionHandle } : {},
      }),
      transformResponse: (res: any) => res?.data ?? res,
    }),

    getPdfData: build.query<PdfRequest[], GetPdfDataRequest>({
      query: ({ ffneId, status }) => ({
        url: `${PS_V1}/ffne/${ffneId}/pdf/requests`,
        params: status ? { status } : {},
      }),
      transformResponse: (res: any) => res?.data ?? res,
      providesTags: (_res, _err, { ffneId }) => [{ type: 'FfnePdf', id: ffneId }],
    }),

    // ── Mutations ──────────────────────────────────────────────────────────────

    createFfne: build.mutation<FfneItem, CreateFfneRequest>({
      query: body => ({ url: `${PS_V1}/ffne`, method: 'POST', body }),
      transformResponse: (res: any) => res?.data ?? res,
      invalidatesTags: ['Ffne'],
    }),

    duplicateFfne: build.mutation<FfneItem, DuplicateFfneRequest>({
      query: ({ ffneId }) => ({ url: `${PS_V1}/ffne/${ffneId}/duplicate`, method: 'POST' }),
      transformResponse: (res: any) => res?.data ?? res,
      invalidatesTags: ['Ffne'],
    }),

    syncFfneAreas: build.mutation<FfneDetailsData, SyncFfneAreasRequest>({
      query: ({ ffneId, data }) => ({
        url: `${PS_V1}/ffne/project/${ffneId}/areas/bulk-sync`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (res: any) => res?.data ?? res,
      invalidatesTags: (_res, _err, { ffneId }) => [{ type: 'Ffne', id: ffneId }],
    }),

    syncFfneProducts: build.mutation<unknown, SyncFfneProductsRequest>({
      query: ({ data }) => ({ url: `${PS_V1}/ffne/sync-products`, method: 'POST', body: data }),
      transformResponse: (res: any) => res?.data ?? res,
    }),

    getCollectionProducts: build.mutation<unknown, GetCollectionProductsRequest>({
      query: ({ data }) => ({
        url: `${PS_V1}/ffne/shopify-collection-products/v2`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (res: any) => res?.data ?? res,
    }),

    patchFfne: build.mutation<FfneItem, PatchFfneRequest>({
      query: ({ ffneId, data }) => ({
        url: `${PS_V1}/ffne/${ffneId}`,
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (res: any) => res?.data ?? res,
      invalidatesTags: ['Ffne'],
    }),

    approveFfne: build.mutation<FfneItem, ApproveFfneRequest>({
      query: ({ ffneId, data }) => ({
        url: `${PS_V1}/ffne/${ffneId}/approve`,
        method: 'POST',
        body: data ?? {},
      }),
      transformResponse: (res: any) => res?.data ?? res,
      invalidatesTags: ['Ffne'],
    }),

    rejectFfne: build.mutation<FfneItem, RejectFfneRequest>({
      query: ({ ffneId, data }) => ({
        url: `${PS_V1}/ffne/${ffneId}/reject`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (res: any) => res?.data ?? res,
      invalidatesTags: ['Ffne'],
    }),

    generateFfnePdf: build.mutation<unknown, GeneratePdfRequest>({
      query: ({ ffneId, data }) => ({
        url: `${PS_V1}/ffne/${ffneId}/pdf/request`,
        method: 'POST',
        body: data ?? {},
      }),
      transformResponse: (res: any) => res?.data ?? res,
      invalidatesTags: (_res, _err, { ffneId }) => [{ type: 'FfnePdf', id: ffneId }],
    }),

    changePdfName: build.mutation<unknown, ChangePdfNameRequest>({
      query: ({ id, data }) => ({
        url: `${PS_V1}/ffne/pdf-request/${id}`,
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (res: any) => res?.data ?? res,
    }),
  }),
});

export const {
  useGetFfneListQuery,
  useGetFfneByIdQuery,
  useGetFfneCategoriesQuery,
  useGetShopifyCollectionFiltersQuery,
  useGetPdfDataQuery,
  useCreateFfneMutation,
  useDuplicateFfneMutation,
  useSyncFfneAreasMutation,
  useSyncFfneProductsMutation,
  useGetCollectionProductsMutation,
  usePatchFfneMutation,
  useApproveFfneMutation,
  useRejectFfneMutation,
  useGenerateFfnePdfMutation,
  useChangePdfNameMutation,
} = ffneApi;
